import { BRSR_BASE_PATH } from '../../config/brsrConfig'
import { sourceById } from '../../data/brsr/regulatorySources'
import type { BrsrResult, BrsrState, Gap } from '../../data/brsr/types'
import { trackAssessmentEvent } from '../../lib/analytics/events'
import { submitBrsrLead } from '../../lib/brsr/lead'
import { renderBrsrPdf } from '../../lib/brsr/pdfReport'
import { saveBrsrState } from '../../lib/brsr/session'
import { pdfFileName, validateLead, validatePdfInput } from '../../lib/brsr/validation'
import { clear, el, errorBox, fieldLabel, helpText } from '../assessment/ui'

export function mountBrsrResults(root: HTMLElement, state: BrsrState): void {
  clear(root)
  if (!state.completed || !state.result) {
    root.append(emptyState())
    return
  }
  trackAssessmentEvent('results_view', { tool: 'india-brsr-readiness' })
  trackAssessmentEvent('lead_form_view', { tool: 'india-brsr-readiness' })
  root.append(renderDashboard(state.result, state))
}

function emptyState(): HTMLElement {
  const card = el('div', { className: 'assess-result-card' })
  card.append(
    el('h2', {}, ['No completed assessment in this session']),
    el('p', {}, [
      'Answers stay in this browser tab only and are cleared when the tab session ends. Start the assessment to see a score, gaps and a PDF.',
    ]),
    el('a', { className: 'btn btn--primary', href: `${BRSR_BASE_PATH}/start` }, ['Start free assessment']),
  )
  return card
}

function renderDashboard(result: BrsrResult, state: BrsrState): HTMLElement {
  const shell = el('div', { className: 'assess-shell' })
  shell.append(el('p', { className: 'assess-pill' }, ['Self-assessment · not a SEBI determination or a compliance rating']))

  const summary = el('div', { className: 'assess-score-row' })
  summary.append(
    metricCard(result.overall.provisional ? 'Provisional score' : 'Readiness score', result.overall.score == null ? '—' : String(result.overall.score), result.overall.bandLabel),
    metricCard('Completion', `${result.overall.completion}%`, `${result.overall.unanswered} required questions unanswered`),
    metricCard('Critical gaps', String(result.overall.criticalGaps), 'Priority 1 items'),
    metricCard('Needs review', String(result.overall.reviewItems), `${result.overall.unknown} answers marked not sure`),
    metricCard('Applicability', statusText(result.applicability.overallStatus), result.financialYearLabel),
  )
  shell.append(summary)

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Applicability']))
  shell.append(
    el('p', { className: 'assess-help' }, [
      'Each conclusion uses your answers and the versioned SEBI rules. The tool does not verify exchange rankings.',
    ]),
  )
  for (const item of result.applicability.conclusions) {
    const card = el('article', { className: 'brsr-conclusion' })
    card.append(
      el('p', { className: `brsr-status brsr-status--${item.status}` }, [statusText(item.status)]),
      el('h3', {}, [item.title]),
      el('p', {}, [item.conclusion]),
      el('p', { className: 'assess-help' }, [item.reason]),
      el('p', { className: 'assess-help' }, [`Year: ${item.financialYear}. Next step: ${item.nextStep}`]),
    )
    const links = el('p', { className: 'brsr-sources' })
    item.sourceIds.forEach((id, index) => {
      const source = sourceById(id)
      if (!source) return
      if (index) links.append(document.createTextNode(' · '))
      links.append(el('a', { href: source.url, target: '_blank', rel: 'noopener noreferrer' }, [source.circularNumber || source.title]))
    })
    card.append(links)
    shell.append(card)
  }

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Dimension scores']))
  for (const dimension of result.dimensions) {
    shell.append(dimensionRow(dimension.label, dimension.score, dimension.coverage, dimension.pathway))
  }

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Three findings']))
  if (result.keyFindings.length === 0) {
    shell.append(el('p', {}, ['No scored gap was produced from the answers. That is only true when responses sit at the documented or repeatable levels.']))
  }
  result.keyFindings.forEach((gap, index) => shell.append(gapCard(gap, index + 1)))

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Critical gaps']))
  const critical = result.gaps.filter((gap) => gap.priority === 'p1')
  if (critical.length === 0) shell.append(el('p', {}, ['No Priority 1 gap was identified from the answers.']))
  critical.forEach((gap) => shell.append(gapCard(gap)))

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Other gaps and the action plan']))
  const rest = result.actions.filter((action) => action.priority !== 'p1')
  if (rest.length === 0) shell.append(el('p', {}, ['No further actions were generated.']))
  const actionList = el('ol', { className: 'brsr-actions' })
  for (const action of result.actions) {
    actionList.append(
      el('li', {}, [
        el('strong', {}, [`${priorityText(action.priority)} · ${action.section}`]),
        el('span', {}, [` ${action.title}. ${action.detail}`]),
      ]),
    )
  }
  shell.append(actionList)

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Strengths']))
  if (result.strengths.length === 0) {
    shell.append(el('p', {}, ['No area was reported with documented evidence or a reviewed repeatable process. This section is left empty on purpose.']))
  } else {
    const list = el('ul', { className: 'brsr-review-list' })
    result.strengths.forEach((item) => list.append(el('li', {}, [`${item.section}: ${item.label} — ${item.responseLabel}`])))
    shell.append(list)
  }

  shell.append(el('h2', { className: 'brsr-block-title' }, ['Indicators to review']))
  if (result.reviewItems.length === 0) shell.append(el('p', {}, ['No “not sure” or contested “not applicable” answers.']))
  else {
    const list = el('ul', { className: 'brsr-review-list' })
    result.reviewItems.forEach((item) => list.append(el('li', {}, [`${item.label} — ${item.responseLabel}. ${item.reason}`])))
    shell.append(list)
  }

  shell.append(el('h2', { className: 'brsr-block-title' }, ['NGRBC principles']))
  for (const principle of result.principleScores) {
    shell.append(dimensionRow(principle.label, principle.score, principle.coverage, principle.pathway))
  }

  shell.append(el('h2', { className: 'brsr-block-title' }, ['A note on systems']))
  shell.append(
    el('p', {}, [
      'Weak activity data, missing emission factors, or no review trail is usually a process problem. A sustainability and carbon-accounting system can store facility data, factors and approvals so the same numbers can feed BRSR tables. It does not file the report or replace an assessment engagement.',
    ]),
  )
  shell.append(
    el('p', {}, [
      el('a', { href: '/platform' }, ['See the EcopsHub platform']),
      document.createTextNode(' · '),
      el('a', { href: '/contact' }, ['Talk to the team']),
    ]),
  )

  shell.append(el('h2', { className: 'brsr-block-title', id: 'brsr-download' }, ['Download the PDF']))
  shell.append(
    el('p', {}, [
      'Your score, gaps and action plan stay on this page. Enter your contact details and consent to be contacted to download the PDF. The full questionnaire is not sent.',
    ]),
  )
  shell.append(leadForm(result, state))

  const revise = el('button', { type: 'button', className: 'btn btn--secondary' }, ['Revise answers']) as HTMLButtonElement
  revise.addEventListener('click', () => {
    state.currentStep = 'evidence'
    state.completed = false
    saveBrsrState(state)
    window.location.href = `${BRSR_BASE_PATH}/start`
  })
  shell.append(el('div', { className: 'assess-nav' }, [revise]))
  return shell
}

function leadForm(result: BrsrResult, state: BrsrState): HTMLFormElement {
  const form = el('form', { className: 'brsr-lead', id: 'brsr-lead', novalidate: true }) as HTMLFormElement
  const status = el('div', { id: 'brsr-lead-status' })
  const name = textField('brsr-name', 'Name', true)
  const email = textField('brsr-email', 'Business email', true, 'email')
  const company = textField('brsr-company', 'Company', true)
  const role = textField('brsr-role', 'Role or department (optional)', false)
  const phone = textField('brsr-phone', 'Phone (optional)', false, 'tel')
  const contact = el('label', { className: 'assess-option assess-option--check' })
  const contactInput = el('input', { type: 'checkbox', className: 'assess-option__input', id: 'brsr-consent-contact' }) as HTMLInputElement
  contact.append(
    contactInput,
    el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
    el('span', { className: 'assess-option__text' }, [
      'I consent to Ecopshub processing my contact details, assessment answers and PDF to deliver the report and follow up about this request.',
    ]),
  )
  const marketing = el('label', { className: 'assess-option assess-option--check' })
  const marketingInput = el('input', { type: 'checkbox', className: 'assess-option__input', id: 'brsr-consent-marketing' }) as HTMLInputElement
  marketing.append(
    marketingInput,
    el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
    el('span', { className: 'assess-option__text' }, ['I also agree to receive occasional product updates. This is optional and separate from the request above.']),
  )
  const companyInput = company.querySelector('input') as HTMLInputElement
  if (result.companyName && result.companyName !== 'Not provided') companyInput.value = result.companyName
  form.append(
    helpText(
      'Contact details, questionnaire answers, results and PDF are stored securely so our team can follow up. Contact details are required to download the PDF.',
    ),
    el('p', {}, [el('a', { href: `${BRSR_BASE_PATH}/privacy` }, ['Privacy note'])]),
    name,
    email,
    company,
    role,
    phone,
    contact,
    marketing,
    status,
    el('button', { type: 'submit', className: 'btn btn--primary' }, ['Submit & download PDF']),
  )
  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    const input = {
      fullName: (form.querySelector('#brsr-name') as HTMLInputElement).value,
      workEmail: (form.querySelector('#brsr-email') as HTMLInputElement).value,
      companyName: (form.querySelector('#brsr-company') as HTMLInputElement).value,
      jobTitle: (form.querySelector('#brsr-role') as HTMLInputElement).value,
      consentContact: contactInput.checked,
    }
    const errors = validateLead(input)
    clear(status)
    if (errors.length) {
      status.append(errorBox(errors))
      return
    }
    const pdfErrors = validatePdfInput(result)
    if (pdfErrors.length) {
      status.append(errorBox(pdfErrors))
      return
    }
    trackAssessmentEvent('lead_form_submit', { tool: 'india-brsr-readiness' })
    const button = form.querySelector('button[type="submit"]') as HTMLButtonElement
    button.disabled = true
    button.textContent = 'Submitting…'
    try {
      const doc = renderBrsrPdf(result)
      const file = pdfFileName(result)
      const pdfBlob = doc.output('blob')
      const response = await submitBrsrLead({
        ...input,
        phone: (form.querySelector('#brsr-phone') as HTMLInputElement).value,
        consentMarketing: marketingInput.checked,
        qualificationSummary: `Applicability ${result.applicability.overallStatus}. ${result.overall.bandLabel}. Score ${result.overall.score ?? 'n/a'}. ${result.financialYearLabel}. Critical gaps ${result.overall.criticalGaps}.`,
        state,
        result,
        pdfBlob,
        pdfFileName: file,
      })
      if (!response.ok) {
        trackAssessmentEvent('lead_form_error', { tool: 'india-brsr-readiness' })
        status.append(el('p', { className: 'assess-error', role: 'status' }, [response.message]))
        return
      }
      trackAssessmentEvent('lead_form_success', { tool: 'india-brsr-readiness' })
      doc.save(file)
      trackAssessmentEvent('report_download', { tool: 'india-brsr-readiness' })
      status.append(
        el('p', { className: 'assess-success', role: 'status' }, [
          response.delivered
            ? 'Details submitted. Your PDF has downloaded. It is a self-assessment, not a filed BRSR.'
            : `${response.message} Your PDF has downloaded.`,
        ]),
      )
    } catch {
      trackAssessmentEvent('lead_form_error', { tool: 'india-brsr-readiness' })
      status.append(
        errorBox(['We could not prepare the PDF or save your details. The on-screen results are still available.']),
      )
    } finally {
      button.disabled = false
      button.textContent = 'Submit & download PDF'
    }
  })
  return form
}

function textField(id: string, label: string, required: boolean, type = 'text'): HTMLElement {
  const wrap = el('div', { className: 'assess-field' })
  const input = el('input', { className: 'assess-input', id, type, name: id, ...(required ? { required: true } : {}) })
  wrap.append(fieldLabel(label, required, id), input)
  return wrap
}

function metricCard(label: string, value: string, detail: string): HTMLElement {
  const card = el('article', { className: 'assess-score-card' })
  card.append(el('p', { className: 'assess-help' }, [label]), el('p', { className: 'assess-big-number' }, [value]), el('p', {}, [detail]))
  return card
}

function dimensionRow(label: string, score: number | null, coverage: number, pathway: string): HTMLElement {
  const row = el('div', { className: 'brsr-dimension' })
  const value = score == null ? pathway.replaceAll('_', ' ') : `${score} / 100`
  row.append(
    el('div', { className: 'brsr-dimension__meta' }, [el('span', {}, [label]), el('strong', {}, [value])]),
    el('p', { className: 'assess-help' }, [`Coverage ${coverage}%`]),
  )
  const bar = el('div', {
    className: 'brsr-bar',
    role: 'progressbar',
    'aria-valuemin': '0',
    'aria-valuemax': '100',
    'aria-valuenow': String(score ?? 0),
    'aria-valuetext': `${label}: ${value}`,
    'aria-label': label,
  })
  const fill = el('span')
  fill.style.width = `${score ?? 0}%`
  bar.append(fill)
  row.append(bar)
  return row
}

function gapCard(gap: Gap, index?: number): HTMLElement {
  const card = el('article', { className: 'assess-result-card' })
  card.append(
    el('p', { className: `brsr-status brsr-status--${gap.priority}` }, [index ? `Finding ${index}` : priorityText(gap.priority)]),
    el('h3', {}, [gap.label]),
    el('p', {}, [`${gap.section}. Your response: ${gap.responseLabel}.`]),
    el('p', {}, [gap.whyItMatters]),
    el('p', {}, [`Suggested action: ${gap.action}`]),
    el('p', { className: 'assess-help' }, [`Priority: ${priorityText(gap.priority)}. Context: ${gap.relation.replaceAll('_', ' ')}.`]),
  )
  if (gap.sourceIds.length) {
    const links = el('p', { className: 'brsr-sources' })
    gap.sourceIds.forEach((id, sourceIndex) => {
      const source = sourceById(id)
      if (!source) return
      if (sourceIndex) links.append(document.createTextNode(' · '))
      links.append(el('a', { href: source.url, target: '_blank', rel: 'noopener noreferrer' }, [source.title]))
    })
    card.append(links)
  }
  return card
}

function statusText(status: string): string {
  if (status === 'verified_rule') return 'Verified rule'
  if (status === 'conditional') return 'Conditional conclusion'
  return 'Requires verification'
}

function priorityText(priority: string): string {
  if (priority === 'p1') return 'Priority 1'
  if (priority === 'p2') return 'Priority 2'
  if (priority === 'p3') return 'Priority 3'
  return 'Optional'
}
